import React, { useContext, useEffect, useMemo, useState } from 'react'
// PowerBI
import 'powerbi-report-authoring'
import { Embed, IReportEmbedConfiguration, models, Page, Report } from 'powerbi-client'
import { PowerBIEmbed } from 'powerbi-client-react'
// Contexts
import { AuthContext } from 'context/AuthContext'
import { ReportContext } from 'context/ReportContext'
// Components
import NavigationTabs from 'components/NavigationBar'
import AdditionalFilters from 'components/AdditionalFilters'
// Helpers
import { getInitialReportConfig, fetchSlicersAndStates, syncSlicers } from 'helpers/report'
import { slicersQueryParams } from 'components/ExportPDF/ExportPDF.helpers'
// Types
import { Filter, PbiTab, ReportFilterType } from 'types/report'
import { PdfSlicers } from 'types/pdfExport'
// Styles
import * as Styled from './Dashboard.styled'

const Dashboard = () => {
    const { embedURL: embedUrl, token: accessToken, tabs, showAdvancedFilters } = useContext(AuthContext)
    const initialReportConfig = getInitialReportConfig({ embedUrl, accessToken })
    const [reportConfig, setReportConfig] = useState<IReportEmbedConfiguration>(initialReportConfig)

    const [report, setReport] = useState<Report>()
    const [reportLoaded, setReportLoaded] = useState(false)
    const [reportSlicerStates, setReportSlicerStates] = useState<models.ISlicerState[]>([])
    const [pdfSlicers, setPdfSlicers] = useState<PdfSlicers>({})

    const tabsList: PbiTab[] = useMemo(() => {
        const allTabs: PbiTab[] = Object.values(tabs)
        const initialPageName = allTabs[0]?.pageName || allTabs[0]?.tabs?.[0]?.pageName
        setReportConfig((prevReportConfig) => ({
            ...prevReportConfig,
            pageName: initialPageName,
        }))
        return allTabs
    }, [tabs])

    useEffect(() => {
        report?.setAccessToken(accessToken).then(() => {
            report?.refresh()
        })
    }, [accessToken])

    useEffect(() => {
        report?.on('loaded', async () => {
            try {
                const filters = await getUpdatedFilters()
                await report.updateFilters(models.FiltersOperations.Replace, filters)
                const hasCurrencyFilter = filters.some(
                    ({ target }) => (target as models.IFilterColumnTarget).column === 'Currency'
                )
                if (hasCurrencyFilter) {
                    await updatePdfCurrencySlicer()
                } else {
                    await setDefaultCurrencySlicer()
                }
            } catch (error) {
                console.log('PBI: Update filters error', error)
            }
            setReportLoaded(true)
        })

        report?.on('rendered', updateSlicersData)
    }, [report])

    const setDefaultCurrencySlicer = async () => {
        const { slicerStates, slicers } = await fetchSlicersAndStates(report!)
        const currencySlicerIndex = slicerStates.findIndex(
            (state) =>
                state.targets?.[0].table.match(/Currency (Conversion|Selector)/) &&
                (state.targets?.[0] as models.IColumnTarget).column === 'Currency'
        )
        if (currencySlicerIndex !== -1) {
            const activePage = await report?.getActivePage()
            const currentTabFilters = tabsList.find((tab) => tab.pageName === activePage?.name)?.filters ?? []
            const currencyFilterValue = currentTabFilters.find((filter) => filter.name === 'Currency')?.value

            if (currencyFilterValue) {
                const currencySlicer = slicers[currencySlicerIndex]
                const currencySlicerState = slicerStates[currencySlicerIndex]
                const currencySlicerFilter = currencySlicerState.filters[0] as models.IBasicFilter
                currencySlicerFilter.values = [currencyFilterValue]
                currencySlicer.setSlicerState(currencySlicerState)
            }
        }
    }

    const getUpdatedFilters = async () => {
        const activePage: Page | undefined = await report?.getActivePage()
        const currentTabFilters: Filter[] = findActiveTab(tabsList, activePage)?.filters ?? []

        const reportFilters = (await report?.getFilters()) as ReportFilterType[]
        return reportFilters?.reduce((acc: ReportFilterType[], current: ReportFilterType) => {
            const currentTabsFilters: Filter | undefined = currentTabFilters?.find(
                (filter: Filter) => (current.target as models.IFilterColumnTarget)?.column === filter.name
            )

            if (currentTabsFilters) {
                if (currentTabsFilters.name.toUpperCase() === 'CURRENCY') {
                    current.operator = 'In'
                }

                current = {
                    ...current,
                    values: [currentTabsFilters.value],
                } as ReportFilterType
            }
            return [...acc, current]
        }, [])
    }

    const updatePdfCurrencySlicer = async () => {
        const reportFilters = await report?.getFilters()
        const currencyFilter = reportFilters?.find(
            (filter) => (filter.target as models.IFilterColumnTarget).column === 'Currency'
        )
        const currencyValue = (currencyFilter as models.IBasicFilter)?.values[0]
        if (currencyValue) {
            setPdfSlicers({ currencyFilter: currencyValue.toString() })
        }
    }

    const findActiveTab = (pbiTabsList: PbiTab[], activePage?: Page): PbiTab | undefined => {
        let activeTab: PbiTab | undefined
        for (const pbiTab of pbiTabsList) {
            if (pbiTab.pageName === activePage?.name) {
                activeTab = pbiTab
                break
            } else if (pbiTab.tabs) {
                const subTabs = Object.values(pbiTab.tabs)
                const foundTab = findActiveTab(subTabs, activePage)
                if (foundTab) {
                    activeTab = foundTab
                    break
                }
            }
        }
        return activeTab
    }

    const updateSlicersData = async () => {
        const activePage = await report?.getActivePage()
        const activeTab = findActiveTab(tabsList, activePage)
        const { slicerStates } = await fetchSlicersAndStates(report!)
        setReportSlicerStates(slicerStates)

        slicerStates.forEach((slicerState) => {
            const slicerTable = slicerState.targets?.[0].table
            const slicerColumn = (slicerState.targets?.[0] as models.IColumnTarget)?.column
            const slicerFilter = slicerState.filters[0] as models.IBasicFilter & models.IAdvancedFilter
            const slicerValues = slicerFilter?.values as string[]

            if (slicerColumn === 'Currency') {
                const currencyFilter = slicerValues[0]
                setPdfSlicers((prevState) => ({ ...prevState, currencyFilter }))
            } else if (
                slicerTable === 'child_subgroup_label' &&
                slicerColumn.toLowerCase().startsWith(slicersQueryParams.subGroupFilters)
            ) {
                const subGroupFilterName = `${slicerColumn.toLowerCase()}_filter`
                setPdfSlicers((prevState) => ({
                    ...prevState,
                    subGroupFilters: { ...prevState.subGroupFilters, [subGroupFilterName]: slicerValues },
                }))
            }

            // Date Slicers
            if (!activeTab?.skipTimeFilter) {
                if (slicerTable === 'Time Period' && slicerColumn === 'Filter') {
                    const timePeriodFilter = slicerValues[0]
                    setPdfSlicers((prevState) => ({ ...prevState, timePeriodFilter }))
                } else if (slicerTable === 'Calendar' && slicerColumn === 'Date' && slicerFilter) {
                    slicerFilter.conditions?.forEach(({ operator, value }) => {
                        if (operator === 'GreaterThanOrEqual') {
                            const startDateFilter = convertTimeStampToISODate(value as string)
                            setPdfSlicers((prevState) => ({ ...prevState, startDateFilter }))
                        } else if (operator === 'LessThan') {
                            const endDateFilter = convertTimeStampToISODate(value as string)
                            setPdfSlicers((prevState) => ({ ...prevState, endDateFilter }))
                        }
                    })
                }
            }
        })
    }

    const updateDashboardSlicers = async (additionalSlicerStates: models.ISlicerState[]) => {
        const { slicers, slicerStates } = await fetchSlicersAndStates(report!)
        syncSlicers({
            targetedSlicers: slicers,
            targetedSlicerStates: slicerStates,
            sourcedSlicerStates: additionalSlicerStates,
        })
    }

    const convertTimeStampToISODate = (isoDateString: string) => {
        const date = new Date(isoDateString)
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0]
    }

    const onChangeTab = async (tab: PbiTab) => {
        const pages: Page[] | undefined = await report?.getPages()
        const requestedPage: Page | undefined = pages?.find((page: Page) => page.name === tab.pageName)

        if (requestedPage) {
            return report?.setPage(requestedPage.name)
        }
    }

    const eventHandlersMap = new Map([
        [
            'loaded',
            function () {
                console.log('PBI: Report has loaded')
            },
        ],
        [
            'rendered',
            function () {
                console.log('PBI: Report has rendered')
            },
        ],
    ])

    return (
        <ReportContext.Provider value={{ pdfSlicers }}>
            {reportLoaded && <NavigationTabs tabs={tabsList} onChangeTab={onChangeTab} />}
            {reportLoaded && showAdvancedFilters && (
                <AdditionalFilters
                    reportSlicerStates={reportSlicerStates}
                    updateDashboardSlicers={updateDashboardSlicers}
                />
            )}
            <Styled.DashboardWrapper showFilterSubHeader={showAdvancedFilters}>
                <Styled.PowerBIiFrame>
                    <PowerBIEmbed
                        embedConfig={reportConfig}
                        eventHandlers={eventHandlersMap}
                        cssClassName={'powerbi-iframe-embed'}
                        getEmbeddedComponent={(embedObject: Embed) => {
                            setReport(embedObject as Report)
                        }}
                    />
                </Styled.PowerBIiFrame>
            </Styled.DashboardWrapper>
        </ReportContext.Provider>
    )
}

export default Dashboard
